'use client'

import React, { useEffect, useId, useState } from 'react'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { TextInput } from '../TextInput'
import { menuStyles, optionStyles } from './Autocomplete.styles'

export type AutocompleteOption = {
  value: string
  label: string
}

export type AutocompleteProps = {
  value: string
  /** Fires as the user types. */
  onChange: (value: string) => void
  /** Fires when a suggestion is chosen (click or keyboard). */
  onSelect: (option: AutocompleteOption) => void
  /** Suggestions to render — the caller supplies/filters these. */
  options: AutocompleteOption[]
  placeholder?: string
  colorScheme?: ColorScheme
  leftIcon?: React.ReactNode
  /** Right-aligned adornment (e.g. a validity check icon). */
  rightAdornment?: React.ReactNode
  disabled?: boolean
  hasWarning?: boolean
  warningText?: string
  width?: string | number
  onBlur?: () => void
  /** Extra attributes forwarded to the underlying input. */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  'data-testid'?: string
}

/**
 * Controlled autocomplete/combobox UI built on the dark {@link TextInput} and a
 * dark suggestion menu. Purely presentational — the caller owns the value and
 * the suggestion list (e.g. wraps it with data-fetching logic). Supports mouse
 * selection and keyboard navigation (Up/Down/Enter/Escape).
 */
export const Autocomplete = ({
  value,
  onChange,
  onSelect,
  options,
  placeholder,
  colorScheme = 'dark',
  leftIcon,
  rightAdornment,
  disabled = false,
  hasWarning,
  warningText,
  width = '100%',
  onBlur,
  inputProps,
  'data-testid': dataTestId,
}: AutocompleteProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const isOpen = isFocused && !isDismissed && options.length > 0

  // Stable ids for the ARIA combobox wiring (input ↔ listbox ↔ active option).
  const baseId = useId()
  const listboxId = `${baseId}-listbox`
  const getOptionId = (i: number) => `${baseId}-option-${i}`
  const activeOptionId =
    isOpen && activeIndex >= 0 ? getOptionId(activeIndex) : undefined

  // Reset the highlighted option whenever the suggestion set changes.
  useEffect(() => {
    setActiveIndex(-1)
  }, [options])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, options.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      const option = options[activeIndex]
      if (option) {
        e.preventDefault()
        onSelect(option)
        setIsDismissed(true)
      }
    } else if (e.key === 'Escape') {
      setIsDismissed(true)
    }
  }

  return (
    <Box
      data-testid={dataTestId}
      onKeyDown={handleKeyDown}
      position='relative'
      width={width}
    >
      <TextInput
        actionButton={rightAdornment}
        colorScheme={colorScheme}
        disabled={disabled}
        hasWarning={hasWarning}
        inputProps={{
          ...inputProps,
          role: 'combobox',
          'aria-autocomplete': 'list',
          'aria-expanded': isOpen,
          'aria-controls': isOpen ? listboxId : undefined,
          'aria-activedescendant': activeOptionId,
        }}
        leftIcon={leftIcon}
        onBlur={() => {
          setIsFocused(false)
          onBlur?.()
        }}
        onFocus={() => {
          setIsFocused(true)
          setIsDismissed(false)
        }}
        onValueChange={(next) => {
          onChange(next)
          setIsDismissed(false)
        }}
        placeholder={placeholder}
        value={value}
        warningText={warningText}
        width='100%'
      />
      {isOpen && (
        <Box {...menuStyles(colorScheme)} id={listboxId} role='listbox'>
          {options.map((option, i) => (
            <Box
              aria-selected={i === activeIndex}
              id={getOptionId(i)}
              key={`${i}-${option.value}`}
              // mouseDown (not click) so selecting fires before the input blur.
              onMouseDown={(e) => {
                e.preventDefault()
                onSelect(option)
                setIsDismissed(true)
              }}
              onMouseEnter={() => setActiveIndex(i)}
              role='option'
              {...optionStyles(colorScheme, i === activeIndex)}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
